import { useRef } from 'react';
import { Platform, Pressable } from 'react-native';
import { EllipsisVertical } from 'lucide-react-native';
import type { TriggerRef } from '@rn-primitives/dropdown-menu';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Icon } from '@/components/ui/icon';
import { Text } from '@/components/ui/text';
import { cn } from '@/lib/utils';
import type { RowMenuProps } from './types';

/**
 * Reusable list-row action menu (web + Android). The row is a `Pressable` (tap
 * runs `onSelect`). On web (no long-press) the ⋮ trigger is always visible; on
 * Android there is no visible affordance — a row long-press opens the menu via
 * the trigger's imperative `open()`.
 *
 * iOS uses the native context menu — see `RowMenu.ios.tsx`.
 */
export const RowMenu = ({
  onSelect,
  actions,
  actionsLabel,
  triggerPlacement = 'overlay',
  className,
  style,
  minHeight,
  isActive = false,
  disabled = false,
  testID,
  triggerTestID,
  children,
}: RowMenuProps) => {
  const triggerRef = useRef<TriggerRef>(null);
  const isAndroid = Platform.OS === 'android';

  return (
    <DropdownMenu>
      <Pressable
        testID={testID}
        accessibilityRole="button"
        accessibilityState={{ selected: isActive, disabled }}
        onPress={disabled ? undefined : onSelect}
        onLongPress={
          isAndroid && !disabled ? () => triggerRef.current?.open() : undefined
        }
        disabled={disabled}
        className={className}
        style={[minHeight ? { minHeight } : null, style]}
      >
        {children}
        {!disabled && (
          <DropdownMenuTrigger ref={triggerRef} asChild>
            <Pressable
              testID={triggerTestID}
              collapsable={false}
              accessibilityLabel={actionsLabel}
              style={isAndroid ? { pointerEvents: 'none' } : undefined}
              className={cn(
                'p-1',
                // `inline` sits in-flow as a vertically-centered trailing slot
                // (row layout is `items-center`) so it never overlaps a
                // right-aligned value; `overlay` pins it to the top-right corner.
                triggerPlacement === 'overlay'
                  ? 'absolute right-2 top-2'
                  : 'items-center justify-center',
                // Android has no pointer — the ⋮ is hidden and long-press opens
                // the menu. On web (no long-press) the ⋮ is always visible.
                isAndroid && 'opacity-0',
              )}
            >
              <Icon
                as={EllipsisVertical}
                size={16}
                className="text-text-muted"
              />
            </Pressable>
          </DropdownMenuTrigger>
        )}
      </Pressable>
      <DropdownMenuContent align="end">
        {actions.map((action) => (
          <DropdownMenuItem
            key={action.key}
            testID={action.testID}
            onPress={action.onPress}
            variant={action.variant}
          >
            <Icon
              as={action.icon}
              size={16}
              className={
                action.variant === 'destructive'
                  ? 'text-destructive'
                  : 'text-foreground'
              }
            />
            <Text>{action.label}</Text>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
